import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faTruck,
  faClock as faClockAlt,
} from '@fortawesome/free-solid-svg-icons';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Helper function to get status icon
const getStatusIcon = (status) => {
  switch (status) {
    case 'delivered':
      return (
        <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2" />
      );
    case 'scheduled':
      return <FontAwesomeIcon icon={faClock} className="text-blue-500 mr-2" />;
    case 'cancelled':
      return (
        <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 mr-2" />
      );
    case 'failed':
      return (
        <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 mr-2" />
      );
    default:
      return (
        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-500 mr-2" />
      );
  }
};

// Helper function to get status text
const getStatusText = (status) => {
  switch (status) {
    case 'delivered':
      return 'Delivered';
    case 'scheduled':
      return 'Scheduled';
    case 'cancelled':
      return 'Cancelled';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
};

// Helper function to get status class
const getStatusClass = (status) => {
  switch (status) {
    case 'delivered':
      return 'bg-green-100 text-green-800';
    case 'scheduled':
      return 'bg-blue-100 text-blue-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper function to get time slot string
const getTimeSlotString = (timeSlotId) => {
  switch (timeSlotId) {
    case 1:
      return '6:00 AM - 9:00 AM';
    case 2:
      return '9:00 AM - 12:00 PM';
    case 3:
      return '12:00 PM - 3:00 PM';
    case 4:
      return '3:00 PM - 6:00 PM';
    case 5:
      return '6:00 PM - 9:00 PM';
    default:
      return 'Time not specified';
  }
};

// Helper function to get recurring pattern text
const getRecurringPatternText = (deliveries, selectedDeliveryDays) => {
  if (!deliveries || deliveries.length === 0) return 'No deliveries scheduled';

  // Get day names from the first few deliveries
  const dayNames = [];
  if (deliveries.length === 1) {
    const date = new Date(deliveries[0].scheduled_date);
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    return `One-time delivery on ${dayName}, ${date.toLocaleDateString(
      'en-US',
      { month: 'short', day: 'numeric' }
    )}`;
  } else {
    // For recurring deliveries, get unique days of the week
    const dayNameMap = new Map();
    deliveries.slice(0, Math.min(7, deliveries.length)).forEach((delivery) => {
      const date = new Date(delivery.scheduled_date);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      dayNameMap.set(date.getDay(), dayName);
    });

    // Sort days by their order in the week
    const sortedDays = Array.from(dayNameMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map((entry) => entry[1]);

    if (sortedDays.length === 1) {
      return `Every ${sortedDays[0]}`;
    } else if (sortedDays.length === 2) {
      return `Every ${sortedDays[0]} and ${sortedDays[1]}`;
    } else {
      const lastDay = sortedDays.pop();
      return `Every ${sortedDays.join(', ')}, and ${lastDay}`;
    }
  }
};

const DeliverySchedule = ({
  deliveries,
  loading,
  error,
  selectedTimeSlot,
  selectedDeliveryDays,
}) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
        <p className="text-gray-500">Loading delivery schedule...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
        {error}
      </div>
    );
  }

  if (!deliveries || deliveries.length === 0) {
    return (
      <div className="text-center py-4 bg-gray-50 rounded-md">
        <FontAwesomeIcon
          icon={faTruck}
          className="text-gray-400 text-3xl mb-2"
        />
        <p className="text-gray-500">No deliveries scheduled</p>
      </div>
    );
  }

  // Make sure we use tomorrow as the earliest possible delivery date
  const ensureStartsFromTomorrow = (deliveries) => {
    if (!deliveries || deliveries.length === 0) return deliveries;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Make a copy of the deliveries so we don't modify the original
    return deliveries.map((delivery) => {
      const deliveryDate = new Date(delivery.scheduled_date);

      if (deliveryDate <= today) {
        // If somehow set to today or earlier, push it to tomorrow
        const adjustedDelivery = { ...delivery };
        adjustedDelivery.scheduled_date = tomorrow.toISOString();
        return adjustedDelivery;
      }

      return delivery;
    });
  };

  // Sort deliveries by date
  const sortDeliveriesByDate = (deliveries) => {
    if (!deliveries || deliveries.length === 0) return [];

    return [...deliveries].sort((a, b) => {
      return new Date(a.scheduled_date) - new Date(b.scheduled_date);
    });
  };

  // Process and prepare deliveries for display
  const processedDeliveries = ensureStartsFromTomorrow(deliveries);
  const sortedDeliveries = sortDeliveriesByDate(processedDeliveries);

  // Use compact format for display
  const recurringPattern = getRecurringPatternText(
    sortedDeliveries,
    selectedDeliveryDays
  );

  const firstDeliveryDate =
    sortedDeliveries.length > 0
      ? new Date(sortedDeliveries[0].scheduled_date)
      : null;

  const lastDeliveryDate =
    sortedDeliveries.length > 0
      ? new Date(sortedDeliveries[sortedDeliveries.length - 1].scheduled_date)
      : null;

  return (
    <div className="space-y-2">
      <div className="bg-white rounded-md border border-gray-200 p-4">
        <div className="flex items-start mb-2">
          <FontAwesomeIcon
            icon={faCalendarAlt}
            className="text-primary mr-2 mt-1"
          />
          <div>
            <div className="font-medium">{recurringPattern}</div>

            {firstDeliveryDate && sortedDeliveries.length > 1 && (
              <div className="text-sm text-gray-600 mt-2">
                <div>
                  <span className="font-medium">First delivery:</span>{' '}
                  {firstDeliveryDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>

                {lastDeliveryDate && (
                  <div className="mt-1">
                    <span className="font-medium">Last delivery:</span>{' '}
                    {lastDeliveryDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {selectedTimeSlot && (
          <div className="text-sm text-gray-600 mt-2 flex items-center">
            <FontAwesomeIcon icon={faClockAlt} className="text-gray-500 mr-2" />
            <span>Delivery time: {getTimeSlotString(selectedTimeSlot)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliverySchedule;
